import { Box, Grid, IconButton } from '@mui/material';
import { memo } from 'react';

import {
  BillingUnit,
  priceFieldLabel,
  ReportType,
  TypologyCategory,
  TypologyPriceType,
} from '../../../../../../../../../api/theHive/typology/enumerations';
import {
  CustomFormControl,
  CustomTextField,
  PreffixNumericTextField,
  SelectTextFieldString,
} from '../../../../../../../../../components/CustomInput';
import {
  IconAddMS,
  IconDeleteMS,
} from '../../../../../../../../../constants/icons';
import {
  selectBillingUnit,
  selectPriceType,
  selectReportType,
  selectTypologyCategory,
} from '../../../constants';
import { StyledDottedDivider, SubsectionTitle } from '../../../styles';
import {
  EditableTypologyGroupFields,
  Typology,
  TypologyGroup,
} from '../../../types';
import TypologyGroupSLA from '../../TypologyGroupSLA';
import TypologyItem from '../../TypologyItem';
import { ActionButton } from '../styles';

interface ServiceItemProps {
  service: TypologyGroup;
  serviceLabel: string;
  domId: string;
  canDeleteService: boolean;
  onServiceChange: <K extends keyof EditableTypologyGroupFields>(
    field: K,
    value: EditableTypologyGroupFields[K]
  ) => void;
  onTypologyChange: <K extends keyof Typology>(
    typologyIndex: number,
    field: K,
    value: Typology[K]
  ) => void;
  onAddTypologyRow: () => void;
  onDeleteTypology: (typologyIndex: number) => void;
  onDeleteService: () => void;
}

const ServiceItem = memo(
  ({
    service,
    serviceLabel,
    domId,
    canDeleteService,
    onServiceChange,
    onTypologyChange,
    onAddTypologyRow,
    onDeleteTypology,
    onDeleteService,
  }: ServiceItemProps) => {
    return (
      <Grid item container id={domId}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          mb="12px"
        >
          <SubsectionTitle sx={{ marginBottom: 0 }}>
            {serviceLabel}
          </SubsectionTitle>
          {canDeleteService && (
            <IconButton
              onClick={onDeleteService}
              title="remover serviço"
              sx={(theme) => ({
                color: '#707070',
                '&:hover': { color: theme.palette.secondary.main },
              })}
            >
              {IconDeleteMS}
            </IconButton>
          )}
        </Box>

        {/* Identificação do serviço */}
        <Grid item container spacing={2}>
          <Grid item xs={4}>
            <SelectTextFieldString
              required
              id={`category-${service.id}`}
              label="categoria"
              value={service.category}
              setValue={(v) =>
                onServiceChange('category', v as TypologyCategory)
              }
              selectOptions={selectTypologyCategory()}
            />
          </Grid>
          <Grid item xs={3}>
            <CustomTextField
              required
              id={`code-${service.id}`}
              label="código"
              value={service.code}
              setValue={(v) => onServiceChange('code', v)}
              maxLength={40}
            />
          </Grid>
          <Grid item xs={5}>
            <CustomTextField
              required
              id={`name-${service.id}`}
              label="nome"
              value={service.name}
              setValue={(v) => onServiceChange('name', v)}
              maxLength={60}
            />
          </Grid>
        </Grid>

        <Grid item container spacing={2} mt="4px">
          <Grid item xs={4}>
            <SelectTextFieldString
              required
              id={`report-type-${service.id}`}
              label="tipo de laudo"
              value={service.reportType}
              setValue={(v) => onServiceChange('reportType', v as ReportType)}
              selectOptions={selectReportType()}
            />
          </Grid>
          <Grid item xs={4}>
            <SelectTextFieldString
              required
              id={`billing-unit-${service.id}`}
              label="cobrança por"
              value={service.billingUnit}
              setValue={(v) => onServiceChange('billingUnit', v as BillingUnit)}
              selectOptions={selectBillingUnit()}
            />
          </Grid>
          <Grid item xs={4}>
            <SelectTextFieldString
              required
              id={`price-type-${service.id}`}
              label="modelo de valor"
              value={service.priceType}
              setValue={(v) =>
                onServiceChange('priceType', v as TypologyPriceType)
              }
              selectOptions={selectPriceType()}
            />
          </Grid>
        </Grid>

        <Grid item container spacing={2} mt="4px">
          <Grid item xs={4}>
            <PreffixNumericTextField
              required
              id={`price-${service.id}`}
              label={priceFieldLabel(service.priceType)}
              prefix="R$ "
              decimalSeparator=","
              decimalScale={2}
              maxLength={16}
              value={service.price}
              setValue={(v) => onServiceChange('price', v)}
            />
          </Grid>
        </Grid>

        {/* Tipos de serviço */}
        <Grid item container mt="16px">
          <SubsectionTitle>Tipos de serviço:</SubsectionTitle>
        </Grid>
        {service.typologies.map((typology, typologyIndex) => {
          const isFirst = typologyIndex === 0;
          const isOnlyOne = service.typologies.length === 1;
          const canDelete =
            !isFirst &&
            (!isOnlyOne || typologyIndex === service.typologies.length - 1);
          return (
            <TypologyItem
              key={typology.id}
              typology={typology}
              typologyIndex={typologyIndex}
              onTypologyChange={onTypologyChange}
              onDeleteTypology={onDeleteTypology}
              canDelete={canDelete}
            />
          );
        })}
        <Grid item container justifyContent="start" mt="8px">
          <ActionButton onClick={onAddTypologyRow}>{IconAddMS}</ActionButton>
        </Grid>

        {/* Prazo */}
        <Grid item container mt="20px">
          <CustomFormControl
            label="prazo definido por projeto"
            isChecked={service.deadlineByProject}
            setIsChecked={(v) => onServiceChange('deadlineByProject', v)}
          />
        </Grid>
        <TypologyGroupSLA group={service} onGroupChange={onServiceChange} />
        <StyledDottedDivider sx={{ marginTop: '20px' }} />
      </Grid>
    );
  }
);

export default ServiceItem;
